import {
  Component, OnInit, OnDestroy, Optional, Inject,
  ChangeDetectorRef, ChangeDetectionStrategy, computed, signal,
} from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { AppService } from '../../../../app.service';
import {
  AddUserDetails,
  AddUserRequest,
  FindUserRequest,
  UserAdminService,
  UserSearchRecord,
  UserSearchResponse,
} from '../../../../users-component/user-admin.service';
import { GtwSnackbarService } from '../../../../shared/_services/gtw-snackbar.service';

interface OptionalFieldDefinition {
  key: 'displayName' | 'phone' | 'title' | 'countryCode' | 'city';
  sourceKey: 'display_name' | 'phone' | 'title' | 'country_code' | 'city';
  label: string;
  maxLength: number;
}

interface OptionalAttribute extends OptionalFieldDefinition {
  value: string;
}

@Component({
    selector: 'gtw-add-user-modal',
    templateUrl: './add-user-modal.component.html',
    styleUrls: ['./add-user-modal.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class AddUserModalComponent implements OnInit, OnDestroy {
  title = 'Add User(s)';
  email_id = '';
  user: any = {};
  display: 'none' | 'create' | 'update' | 'error' = 'none';
  loading = false;
  updating = false;
  minimise = false;
  active = 1;
  avatarError = false;
  readonly first_name = signal('');
  readonly last_name = signal('');
  readonly formEmail = signal('');
  readonly selectedOptionalKey = signal('');
  readonly optionalDraft = signal('');
  readonly optionalAttributes = signal<OptionalAttribute[]>([]);

  user_types = [
    { label: 'User', value: 1 },
    { label: 'Editor', value: 2 },
    { label: 'Admin', value: 3 },
    { label: 'Developer', value: 4 },
    { label: 'Super Admin', value: 5 },
  ];
  user_type = this.user_types[0];

  readonly optionalCatalog: OptionalFieldDefinition[] = [
    { key: 'displayName', sourceKey: 'display_name', label: 'Display Name', maxLength: 100 },
    { key: 'phone', sourceKey: 'phone', label: 'Phone', maxLength: 50 },
    { key: 'title', sourceKey: 'title', label: 'Title', maxLength: 500 },
    { key: 'countryCode', sourceKey: 'country_code', label: 'Country Code', maxLength: 10 },
    { key: 'city', sourceKey: 'city', label: 'City', maxLength: 50 },
  ];

  readonly availableOptionalFields = computed(() => {
    const selected = new Set(this.optionalAttributes().map((field) => field.key));
    return this.optionalCatalog.filter((field) => !selected.has(field.key));
  });

  readonly selectedOptionalField = computed(() =>
    this.availableOptionalFields().find((field) => field.key === this.selectedOptionalKey())
  );

  settings: any = {};

  private destroy$ = new Subject<void>();

  constructor(
    @Optional() private dialogRef: MatDialogRef<AddUserModalComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) private data: any,
    private appService: AppService,
    private userAdminService: UserAdminService,
    private snackbar: GtwSnackbarService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.settings = this.appService.SERVER_CONFIG?.configSettings || {};
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get avatarUrl(): string {
    if (!this.user?.sso_id || !this.settings.avatar_url) return '';
    return `${this.settings.avatar_url}/86/${this.user.sso_id}.jpg`;
  }

  get canSave(): boolean {
    return this.isWithin(this.formEmail(), 200)
      && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.formEmail().trim())
      && this.isWithin(this.first_name(), 50)
      && this.isWithin(this.last_name(), 50)
      && !!this.user_type
      && this.optionalAttributes().every((field) => field.value.trim().length > 0 && field.value.trim().length <= field.maxLength);
  }

  findUser(): void {
    if (!this.email_id.trim()) return;

    this.user = {};
    this.display = 'none';
    this.avatarError = false;
    this.loading = true;
    this.resetCreateForm();
    this.cdr.detectChanges();

    const payload: FindUserRequest = {
      user_id: '',
      email_id: this.email_id.trim(),
    };

    this.userAdminService.findUser(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: UserSearchResponse) => {
          this.loading = false;
          if (res?.callSuccess === '1' && (res.searchStatus === 'GTW_USER' || res.user?.in_system === 1)) {
            this.user = res.user || {};
            this.display = 'update';
          } else if (res?.callSuccess === '1' && (res.searchStatus === 'LDAP_USER' || res.searchStatus === 'NOT_FOUND')) {
            this.openCreateForm(res.user);
          } else {
            this.display = 'error';
          }
          this.cdr.detectChanges();
        },
        error: () => {
          this.loading = false;
          this.display = 'error';
          this.cdr.detectChanges();
        },
      });
  }

  onEmailKeyup(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      this.findUser();
    }
  }

  addOptionalField(): void {
    const definition = this.selectedOptionalField();
    const value = this.optionalDraft().trim();
    if (!definition || !value || value.length > definition.maxLength) {
      return;
    }
    this.optionalAttributes.update((fields) => [...fields, { ...definition, value }]);
    this.selectedOptionalKey.set('');
    this.optionalDraft.set('');
  }

  removeOptionalField(key: OptionalFieldDefinition['key']): void {
    this.optionalAttributes.update((fields) => fields.filter((field) => field.key !== key));
  }

  addUser(): void {
    if (!this.canSave) {
      return;
    }
    if (this.updating) {
      this.snackbar.openSnackBar('One moment, request is still processing.', 'gtw-snackbar--yellow');
      return;
    }

    this.updating = true;
    this.cdr.detectChanges();

    const optionalAttributes: Record<string, string> = {};
    this.optionalAttributes().forEach((field) => {
      optionalAttributes[field.key] = field.value.trim();
    });
    const userDetails: AddUserDetails = {
      email: this.formEmail().trim(),
      firstName: this.first_name().trim(),
      lastName: this.last_name().trim(),
      active: this.active === 1,
      userTypeKey: this.user_type.value,
      optionalAttributes,
    };
    const payload: AddUserRequest = {
      user_details: JSON.stringify(userDetails),
    };

    this.userAdminService.addUser(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: UserSearchResponse) => {
          this.updating = false;
          const created = res?.user;
          if (res && res.callSuccess === '1' && created?.sso_id) {
            this.snackbar.openSnackBar('User has been added', 'gtw-snackbar--green');
            this.editUser(created.sso_id, created.client_key);
          } else {
            this.snackbar.openSnackBar(res?.errorMessage || 'Failed to add user', 'gtw-snackbar--red');
            this.cdr.detectChanges();
          }
        },
        error: () => {
          this.updating = false;
          this.snackbar.openSnackBar('Error adding user', 'gtw-snackbar--red');
          this.cdr.detectChanges();
        },
      });
  }

  editUser(sso_id: string, client_key?: string): void {
    this.dialogRef?.close({ action: 'editUser', sso_id, client_key });
  }

  compareByValue(a: any, b: any): boolean {
    return a && b && a.value === b.value;
  }

  cancel(): void {
    this.dialogRef?.close();
  }

  private openCreateForm(prefill?: UserSearchRecord): void {
    this.resetCreateForm();
    const email = (prefill?.email || this.email_id || '').trim();
    this.formEmail.set(email);
    const nameFromEmail = this.namesFromEmail(email);
    this.first_name.set(((prefill?.first_name || '').trim() || nameFromEmail.firstName).slice(0, 50));
    this.last_name.set(((prefill?.last_name || '').trim() || nameFromEmail.lastName).slice(0, 50));
    const prefilled: OptionalAttribute[] = [];
    this.optionalCatalog.forEach((field) => {
      const value = String(prefill?.[field.sourceKey] || '').trim();
      if (value) {
        prefilled.push({ ...field, value: value.slice(0, field.maxLength) });
      }
    });
    this.optionalAttributes.set(prefilled);
    this.display = 'create';
  }

  private resetCreateForm(): void {
    this.first_name.set('');
    this.last_name.set('');
    this.formEmail.set('');
    this.active = 1;
    this.user_type = this.user_types[0];
    this.optionalAttributes.set([]);
    this.selectedOptionalKey.set('');
    this.optionalDraft.set('');
  }

  private namesFromEmail(email: string): { firstName: string; lastName: string } {
    const localPart = email.split('@')[0] || '';
    const parts = localPart.split(/[._-]+/).filter((part) => /[A-Za-z]/.test(part));
    if (!parts.length) {
      return { firstName: '', lastName: '' };
    }
    const capitalize = (part: string) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
    return {
      firstName: capitalize(parts[0]),
      lastName: parts.length > 1 ? capitalize(parts[parts.length - 1]) : '',
    };
  }

  private isWithin(value: string, maxLength: number): boolean {
    const normalized = value.trim();
    return normalized.length > 0 && normalized.length <= maxLength;
  }
}
